import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert'
import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { Card, CardContent } from '@repo/ui/card'
import {
	getAdminCatalogNeedsCatalogIdCandidates,
	getAdminCatalogPrintings,
	postAdminCatalogNeedsCatalogIdMatch,
	type GetAdminCatalogNeedsCatalogIdCandidatesResponse,
	type GetAdminCatalogPrintingsResponse
} from '@repo/sdk/gen'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Check, Plus } from 'lucide-react'
import { useState } from 'react'
import { BackofficeShell } from '@/components/backoffice-shell'
import { CatalogThumbnail } from '@/components/catalog-thumbnail'
import {
	QueryErrorState,
	QueryLoadingState
} from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'
import { DebouncedSearchInput } from '../-components/debounced-search-input'

type CatalogReview = GetAdminCatalogNeedsCatalogIdCandidatesResponse['data']
type SuggestedPrinting = CatalogReview['candidates'][number]
type ProviderCatalogItem = NonNullable<CatalogReview['item']>
type CatalogPrinting = GetAdminCatalogPrintingsResponse['data'][number]
interface MatchOption {
	id: string
	imageUrl: string | null
	label: string
	meta: string
	rarity: string | null
}

export const Route = createFileRoute('/queue/$id')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	component: CandidateReview
})

function CandidateReview() {
	const { t } = useTranslation()
	const { id } = Route.useParams()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [catalogSearch, setCatalogSearch] = useState<string | null>(null)
	const candidatesQuery = useQuery({
		queryKey: ['catalog', 'needs-catalog', id, 'candidates'],
		queryFn: () =>
			getAdminCatalogNeedsCatalogIdCandidates({ path: { id } }).then(
				(response) => response.data.data
			)
	})
	const currentCatalogSearch =
		catalogSearch ?? candidatesQuery.data?.item?.cardNumber ?? ''
	const catalogPrintingsQuery = useQuery({
		enabled: candidatesQuery.isSuccess,
		queryKey: [
			'catalog',
			'printings',
			'match-search',
			currentCatalogSearch
		],
		queryFn: () =>
			getAdminCatalogPrintings({
				query: {
					limit: 20,
					offset: 0,
					q: currentCatalogSearch || undefined,
					sortBy: 'cardNumber'
				}
			}).then((response) => response.data)
	})
	const matchMutation = useMutation({
		mutationFn: (printingId: string) =>
			postAdminCatalogNeedsCatalogIdMatch({
				path: { id },
				body: { printingId }
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ['catalog', 'needs-catalog']
			})
			navigate({ to: '/queue' })
		}
	})

	return (
		<BackofficeShell contentClassName='max-w-4xl'>
			<Link
				className='inline-flex items-center gap-2 text-muted-foreground text-sm hover:text-foreground'
				to='/queue'
			>
				<ArrowLeft className='size-4' />
				{t('common:reconciliation')}
			</Link>
			<header className='mt-8 max-w-2xl'>
				<h1 className='font-semibold text-3xl tracking-tight'>
					{t('common:review_suggested_printings')}
				</h1>
				<p className='mt-2 text-muted-foreground'>
					{t('common:review_match_copy')}
				</p>
			</header>
			<div className='mt-8 space-y-6'>
				{candidatesQuery.isPending ? (
					<QueryLoadingState message={t('common:loading')} />
				) : null}
				{candidatesQuery.isError ? (
					<QueryErrorState
						message={t('common:queue_load_error')}
						onRetry={() => candidatesQuery.refetch()}
					/>
				) : null}
				{matchMutation.isError ? (
					<Alert variant='destructive'>
						<AlertTitle>{t('common:match_error')}</AlertTitle>
						<AlertDescription>
							{t('common:match_error_copy')}
						</AlertDescription>
					</Alert>
				) : null}
				{!(candidatesQuery.isPending || candidatesQuery.isError) ? (
					<>
						{candidatesQuery.data?.item ? (
							<ProviderProductSummary
								item={candidatesQuery.data.item}
							/>
						) : null}
						<CandidateSuggestions
							candidates={candidatesQuery.data?.candidates ?? []}
							disabled={matchMutation.isPending}
							onMatch={(printingId) =>
								matchMutation.mutate(printingId)
							}
						/>
					</>
				) : null}
				<Card className='rounded-2xl'>
					<CardContent className='space-y-5 p-5 sm:p-6'>
						<div>
							<h2 className='font-semibold text-lg'>
								{t('common:search')}
							</h2>
							<p className='mt-1 text-muted-foreground text-sm'>
								{t('common:catalog_search_for_match_copy')}
							</p>
						</div>
						<DebouncedSearchInput
							ariaLabel={t('common:search_existing_printings')}
							className='w-full sm:max-w-sm'
							onValueChange={setCatalogSearch}
							placeholder={t('common:search_existing_printings')}
							value={currentCatalogSearch}
						/>
						{catalogPrintingsQuery.isPending ? (
							<QueryLoadingState message={t('common:loading')} />
						) : null}
						{catalogPrintingsQuery.isError ? (
							<QueryErrorState
								message={t('common:catalog_search_error')}
								onRetry={() => catalogPrintingsQuery.refetch()}
							/>
						) : null}
						{!(
							catalogPrintingsQuery.isPending ||
							catalogPrintingsQuery.isError
						) ? (
							<CatalogSearchResults
								disabled={matchMutation.isPending}
								onMatch={(printingId) =>
									matchMutation.mutate(printingId)
								}
								printings={
									catalogPrintingsQuery.data?.data ?? []
								}
							/>
						) : null}
					</CardContent>
				</Card>
				<Card className='rounded-2xl border-dashed'>
					<CardContent className='flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6'>
						<div>
							<p className='font-medium'>
								{t('common:catalog_record_not_found')}
							</p>
							<p className='mt-1 text-muted-foreground text-sm'>
								{t('common:create_catalog_record_copy')}
							</p>
						</div>
						<Button
							nativeButton={false}
							render={<Link to='/printings' />}
							variant='outline'
						>
							<Plus />
							{t('common:create_catalog_record')}
						</Button>
					</CardContent>
				</Card>
			</div>
		</BackofficeShell>
	)
}

function ProviderProductSummary(props: { item: ProviderCatalogItem }) {
	const { t } = useTranslation()

	return (
		<Card className='rounded-2xl border-primary/20 bg-primary/5'>
			<CardContent className='flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:p-6'>
				<CatalogThumbnail src={props.item.imageUrl} />
				<div className='min-w-0 flex-1'>
					<p className='font-medium text-primary text-sm'>
						{t('common:provider_product')}
					</p>
					<p className='mt-1 truncate font-semibold text-lg'>
						{props.item.name ?? props.item.providerProductId}
					</p>
					<p className='mt-1 truncate text-muted-foreground text-sm'>
						{props.item.provider} · {props.item.providerProductId}
					</p>
				</div>
				<dl className='grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:min-w-64'>
					<CatalogReference
						label={t('common:card_number')}
						value={props.item.cardNumber}
					/>
					<CatalogReference
						label={t('common:expansion')}
						value={props.item.episodeCode}
					/>
					<CatalogReference
						label={t('common:provider_version')}
						value={props.item.providerVersion}
					/>
				</dl>
			</CardContent>
		</Card>
	)
}

function CatalogReference(props: { label: string; value: string | null }) {
	return (
		<div>
			<dt className='text-muted-foreground text-xs'>{props.label}</dt>
			<dd className='mt-1 font-medium'>{props.value ?? '—'}</dd>
		</div>
	)
}

function CandidateSuggestions(props: {
	candidates: SuggestedPrinting[]
	disabled: boolean
	onMatch: (printingId: string) => void
}) {
	const { t } = useTranslation()

	if (props.candidates.length === 0) {
		return (
			<Card className='rounded-2xl border-dashed'>
				<CardContent className='p-5 text-muted-foreground text-sm sm:p-6'>
					{t('common:no_suggested_match_copy')}
				</CardContent>
			</Card>
		)
	}

	return (
		<section aria-labelledby='suggested-matches-heading'>
			<h2
				className='mb-3 font-semibold text-lg'
				id='suggested-matches-heading'
			>
				{t('common:suggested_matches')}
			</h2>
			<div className='space-y-3'>
				{props.candidates.map((candidate) => (
					<PrintingMatchCard
						disabled={props.disabled}
						key={candidate.id}
						onMatch={props.onMatch}
						printing={{
							id: candidate.id,
							imageUrl: candidate.imageUrl,
							label:
								candidate.cardNumber ??
								candidate.printedNumber ??
								candidate.id,
							meta: `${candidate.expansionCode} · ${candidate.variant}`,
							rarity: candidate.rarity
						}}
					/>
				))}
			</div>
		</section>
	)
}

function CatalogSearchResults(props: {
	disabled: boolean
	onMatch: (printingId: string) => void
	printings: CatalogPrinting[]
}) {
	const { t } = useTranslation()

	if (props.printings.length === 0) {
		return (
			<p className='rounded-xl border border-dashed p-5 text-muted-foreground text-sm'>
				{t('common:catalog_search_empty')}
			</p>
		)
	}

	return (
		<div className='space-y-3'>
			{props.printings.map((printing) => (
				<PrintingMatchCard
					disabled={props.disabled}
					key={printing.id}
					onMatch={props.onMatch}
					printing={{
						id: printing.id,
						imageUrl: printing.imageUrl,
						label:
							printing.card.cardNumber ??
							printing.printedNumber ??
							printing.id,
						meta: [
							printing.expansion.code,
							printing.printedNumber,
							printing.language,
							printing.variant
						]
							.filter(Boolean)
							.join(' · '),
						rarity: printing.rarity
					}}
				/>
			))}
		</div>
	)
}

function PrintingMatchCard(props: {
	disabled: boolean
	onMatch: (printingId: string) => void
	printing: MatchOption
}) {
	const { t } = useTranslation()

	return (
		<div className='flex flex-col gap-4 rounded-xl border bg-muted/20 p-4 sm:flex-row sm:items-center'>
			<CatalogThumbnail src={props.printing.imageUrl} />
			<div className='min-w-0 flex-1'>
				<p className='font-medium'>{props.printing.label}</p>
				<p className='mt-1 text-muted-foreground text-sm'>
					{props.printing.meta}
				</p>
			</div>
			<div className='flex items-center gap-3 sm:justify-end'>
				{props.printing.rarity ? (
					<Badge variant='secondary'>{props.printing.rarity}</Badge>
				) : null}
				<Button
					disabled={props.disabled}
					onClick={() => props.onMatch(props.printing.id)}
					size='sm'
				>
					<Check />
					{t('common:match_this_printing')}
				</Button>
			</div>
		</div>
	)
}
